import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { get, throttle } from 'lodash'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Box, Paper, Grid, useMediaQuery, Stack, Typography } from '@mui/material'
import Loading from 'components/generic/Loading'
import { useSearchBar } from 'utils/custom-hooks/useSearchBar'
import InboundTicketsGridCard from 'components/containers/inbound-tickets/components/InboundTicketsGridCard'
import { useGetOrgImageQuery } from 'services/assets'
import { ImageNotSupported as ImageNotSupportedIcon } from '@mui/icons-material'

const skeletonArray = [...Array(20).keys()].map(value => ({ key: value.toString() }))

const InboundTicketsGridView = ({
  rows,
  loading,
  renderViewModeToggle,
  renderDateRangePicker,
  useBackendSortAndFilter,
  filter,
  setFilter,
  allowedOptions,
  renderSelectFilter,
  selectFilterPath,
  setQueryOptions,
}) => {
  const { logo } = useSelector(st => st.settings.details)
  const {
    data: logoImage,
    isLoading: isLoadingLogoImage,
    error: logoImageError,
  } = useGetOrgImageQuery({ filename: logo }, { skip: !logo })

  const xs = useMediaQuery(query => query.breakpoints.up('xs'), { noSsr: true })
  const sm = useMediaQuery(query => query.breakpoints.up('sm'), { noSsr: true })
  const md = useMediaQuery(query => query.breakpoints.up('md'), { noSsr: true })
  const lg = useMediaQuery(query => query.breakpoints.up('lg'), { noSsr: true })
  const xl = useMediaQuery(query => query.breakpoints.up('xl'), { noSsr: true })
  const rowSize = useMemo(() => {
    switch (true) {
      case xl:
        return 5
      case lg:
        return 4
      case md:
        return 3
      case sm:
        return 2
      case xs:
        return 1
      default:
        return 0
    }
  }, [xs, sm, md, lg, xl])

  const { sideBarWidth } = useSelector(state => state.ui)

  // Use a page size based on the visible grid cards (e.g., rowSize * 3)
  const ITEMS_PER_PAGE = rowSize * 4

  // Track the current page (starting at 1)
  const [currentPage, setCurrentPage] = useState(1)

  // Track accumulated tickets
  const [accumulatedTickets, setAccumulatedTickets] = useState([])
  const [hasMore, setHasMore] = useState(true)

  // Get the search bar (we're no longer using filteredRows)
  const { renderSearchBar } = useSearchBar({
    textSearchPlaceholder: 'Search all fields',
    rows,
    renderViewModeToggle,
    renderDateRangePicker,
    renderSelectFilter,
    useBackendSortAndFilter,
    filter,
    setFilter,
  })

  // Reset accumulated tickets when filter changes
  useEffect(() => {
    setAccumulatedTickets([])
    setHasMore(true)
    setCurrentPage(1)
    setQueryOptions(prevQuery => ({
      ...prevQuery,
      pageSize: ITEMS_PER_PAGE,
      page: 1,
    }))
  }, [filter, ITEMS_PER_PAGE, setQueryOptions])

  // Update accumulated tickets when new rows come in (triggered by changes in rows)
  useEffect(() => {
    if (!loading) {
      if (currentPage === 1) {
        setAccumulatedTickets(rows)
      } else {
        // Merge new rows with existing ones, ensuring uniqueness
        setAccumulatedTickets(prev => {
          const uniqueTickets = new Map()
          prev.forEach(ticket => {
            uniqueTickets.set(ticket.id, ticket)
          })
          rows.forEach(ticket => {
            uniqueTickets.set(ticket.id, ticket)
          })
          return Array.from(uniqueTickets.values())
        })
      }
      // If fewer items than expected are returned, there's no more data to load
      setHasMore(rows.length === ITEMS_PER_PAGE)
    }
  }, [rows, loading])

  // IntersectionObserver callback to lazy load the next page,
  // throttled to prevent rapid firing
  const observerCallback = useCallback(
    throttle(
      entries => {
        if (entries?.[0]?.isIntersecting && !loading && hasMore) {
          setCurrentPage(prevPage => {
            const nextPage = prevPage + 1
            setQueryOptions(prevQuery => ({
              ...prevQuery,
              pageSize: ITEMS_PER_PAGE,
              page: nextPage,
            }))
            return nextPage
          })
        }
      },
      300,
      { leading: true, trailing: false },
    ),
    [loading, hasMore, setQueryOptions, ITEMS_PER_PAGE],
  )

  // Additional filtering based on selectFilterPath, if provided
  const gridCards = useMemo(() => {
    const filtered = selectFilterPath
      ? accumulatedTickets.filter(row => allowedOptions.includes(get(row, selectFilterPath)?.toUpperCase()))
      : accumulatedTickets

    // If filtering results in an empty array, stop further loading
    if (filtered.length === 0) {
      setHasMore(false)
    }

    return filtered
  }, [accumulatedTickets, selectFilterPath, allowedOptions])

  const bottomOfPageRef = useRef(null)
  useEffect(() => {
    // Increase rootMargin to pre-load the next page(s) before the user reaches the bottom
    const observer = new IntersectionObserver(observerCallback, { rootMargin: '400px' })
    if (bottomOfPageRef.current) {
      observer.observe(bottomOfPageRef.current)
    }
    return () => {
      if (bottomOfPageRef.current) {
        observer.unobserve(bottomOfPageRef.current)
      }
    }
  }, [observerCallback, gridCards])

  useEffect(() => {
    window.scroll(0, 0)
  }, [])

  return (
    <Paper
      elevation={0}
      sx={{ width: '100%', height: '100%', backgroundColor: 'background.transparent' }}
      data-testid="ibt-grid-view"
    >
      <Box
        mb={2}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {renderSearchBar()}
      </Box>
      {logoImageError ? (
        <Stack alignItems="center" justifyContent="center" height="100%" sx={{ color: 'text.hint' }}>
          <ImageNotSupportedIcon />
          <Typography fontSize="small">Error capturing image.</Typography>
          <Typography fontSize="small">Please try again.</Typography>
        </Stack>
      ) : (
        <Grid container rowSpacing={2} columnSpacing={2} maxWidth={`calc(100vw - ${sideBarWidth}px - 24px)`}>
          {((loading || isLoadingLogoImage) && currentPage === 1 ? skeletonArray : gridCards).map(
            (ticketData, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={`${ticketData.key || ticketData.id}-${index}`}>
                <InboundTicketsGridCard logoImage={logoImage} {...ticketData} />
              </Grid>
            ),
          )}
        </Grid>
      )}
      {!loading && gridCards.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" height="100%" sx={{ pt: 10, color: 'text.hint' }}>
          <Typography>No results found</Typography>
        </Stack>
      ) : null}
      {hasMore ? (
        <Grid pt={4} pb={4} alignItems="center" ref={bottomOfPageRef}>
          <Loading localized localizedSx={{ height: '100px' }} />
        </Grid>
      ) : null}
    </Paper>
  )
}

InboundTicketsGridView.propTypes = {
  rows: PropTypes.array,
  loading: PropTypes.bool,
  renderViewModeToggle: PropTypes.func,
  renderDateRangePicker: PropTypes.func,
  renderSelectFilter: PropTypes.func,
  useBackendSortAndFilter: PropTypes.bool,
  filter: PropTypes.object,
  setFilter: PropTypes.func,
  setQueryOptions: PropTypes.func,
  allowedOptions: PropTypes.array,
  selectFilterPath: PropTypes.string,
}

InboundTicketsGridView.defaultProps = {
  rows: [],
  loading: false,
  renderViewModeToggle: () => null,
  renderDateRangePicker: () => null,
  renderSelectFilter: () => null,
  useBackendSortAndFilter: false,
  filter: {},
  setFilter: () => {},
  setQueryOptions: () => {},
  allowedOptions: [],
  selectFilterPath: '',
}

export default InboundTicketsGridView
